import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
  Divider,
  Tab,
  Tabs,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
  PersonAdd,
  Login
} from '@mui/icons-material';
import { adminLogin, adminRegister } from '../../services/adminApi';
import { showLoadingToast, updateToast } from '../../utils/toast';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useNavigate } from 'react-router-dom';

const AdminLogin = () => {
  const { login, admin, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  
  // Login form state
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });
  
  // Register form state
  const [registerForm, setRegisterForm] = useState({
    username: '',
    password: '',
    email: '',
    fullName: '',
    isSuperadmin: false
  });

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (!loading && admin) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [admin, loading, navigate]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ width: 40, height: 40, border: '3px solid #f3f3f3', borderTop: '3px solid #1976d2', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            <Typography variant="body1" sx={{ mt: 2 }}>Loading...</Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    
    if (!loginForm.username || !loginForm.password) {
      setError('Please fill in all fields');
      return;
    }

    const toastId = showLoadingToast('Signing in...');

    try {
      const response = await adminLogin(loginForm);
      
      // Use admin auth context login method
      login(response.data);
      
      updateToast(toastId, 'Login successful!', 'success');
      
      // Redirect to admin dashboard using React Router immediately
      navigate('/admin/dashboard');
      
    } catch (error) {
      updateToast(toastId, 'Login failed', 'error');
      setError(error.response?.data?.message || error.response?.data?.error || 'Invalid credentials');
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!registerForm.username || !registerForm.password || !registerForm.email || !registerForm.fullName) {
      setError('Please fill in all fields');
      return;
    }
    
    if (registerForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    const toastId = showLoadingToast('Creating admin account...');

    try {
      const response = await adminRegister(registerForm);
      
      updateToast(toastId, 'Admin account created successfully!', 'success');
      
      // Switch to login tab
      setTabValue(0);
      setRegisterForm({
        username: '',
        password: '',
        email: '',
        fullName: '',
        isSuperadmin: false
      });
      
      setError('');
      
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      updateToast(toastId, 'Registration failed', 'error');
      setError(error.response?.data?.message || error.response?.data?.error || 'Failed to create admin account');
    }
  };

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: 4
        }}
      >
        <Paper
          elevation={24}
          sx={{
            width: '100%',
            maxWidth: 500,
            borderRadius: 4,
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              p: 3,
              textAlign: 'center'
            }}
          >
            <AdminPanelSettings sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              Admin Portal
            </Typography>
            <Typography variant="body2">
              Secure Admin Management System
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {/* Tabs */}
            {/* <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    minHeight: 60,
                    fontSize: '1rem',
                    fontWeight: 500
                  }
                }}
              >
                <Tab icon={<Login />} label="Login" />
                <Tab icon={<PersonAdd />} label="Register" />
              </Tabs>
            </Box> */}

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Login Form */}
            {tabValue === 0 && (
              <Box component="form" onSubmit={handleLoginSubmit}>
                <TextField
                  fullWidth
                  label="Username"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  margin="normal"
                  required
                  autoFocus
                />
                
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  margin="normal"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                >
                  Sign In
                </Button>
              </Box>
            )}

            {/* Register Form */}
            {tabValue === 1 && (
              <Box component="form" onSubmit={handleRegisterSubmit}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={registerForm.fullName}
                  onChange={(e) => setRegisterForm({ ...registerForm, fullName: e.target.value })}
                  margin="normal"
                  required
                  autoFocus
                />
                
                <TextField
                  fullWidth
                  label="Username"
                  value={registerForm.username}
                  onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
                  margin="normal"
                  required
                />
                
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  margin="normal"
                  required
                />
                
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                  margin="normal"
                  required
                  helperText="Minimum 6 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                >
                  Create Admin Account
                </Button>
              </Box>
            )}
          </CardContent>
        </Paper>
      </Box>
      
      {/* Link to merchant login */}
      {/* <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Are you a merchant? 
          <Button 
            component="a"
            href="/login"
            variant="text"
            size="small"
            sx={{ ml: 1, textTransform: 'none' }}
          >
            Go to Merchant Login
          </Button>
        </Typography>
      </Box> */}
    </Container>
  );
};

export default AdminLogin;
